import { createContext, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { isStartupBenefitsPublicPath } from "@shared/startupBenefitsPublic";
import { SIDE_EVENTS_HOST } from "@shared/sideEventsHub";

interface DomainContextData {
  id: number | null;
  hostname: string;
  contentArea: string;
  label: string | null;
  cohortProgramId: number | null;
  routePrefix: string | null;
  defaultRoute: string;
  brandingOverrides: {
    siteName?: string;
    logoUrl?: string;
    faviconUrl?: string;
    primaryColor?: string;
    headerHtml?: string;
    footerHtml?: string;
  } | null;
  allowedRoutes: string[] | null;
  blockedRoutes: string[] | null;
  isPrimary: boolean;
}

interface DomainContextValue {
  domain: DomainContextData;
  isLoading: boolean;
  isMainSite: boolean;
  isEwit: boolean;
  isCohortDomain: boolean;
  isChiefInAI: boolean;
  isRouteAllowed: (path: string) => boolean;
}

const defaultDomain: DomainContextData = {
  id: null,
  hostname: window.location.hostname,
  contentArea: "main",
  label: null,
  cohortProgramId: null,
  routePrefix: null,
  defaultRoute: "/",
  brandingOverrides: null,
  allowedRoutes: null,
  blockedRoutes: null,
  isPrimary: true,
};

const DomainContext = createContext<DomainContextValue>({
  domain: defaultDomain,
  isLoading: true,
  isMainSite: true,
  isEwit: false,
  isCohortDomain: false,
  isChiefInAI: false,
  isRouteAllowed: () => true,
});

function matchRoute(pattern: string, path: string): boolean {
  if (pattern === path) return true;
  if (pattern.endsWith("/*")) {
    const prefix = pattern.slice(0, -2);
    return path === prefix || path.startsWith(prefix + "/");
  }
  if (pattern.endsWith("*")) {
    return path.startsWith(pattern.slice(0, -1));
  }
  return false;
}

export function DomainProvider({ children }: { children: React.ReactNode }) {
  const isIsolatedSideEventsHost = window.location.hostname === SIDE_EVENTS_HOST;
  const { data, isLoading } = useQuery<{ domainContext: DomainContextData; hostname: string }>({
    queryKey: ["/api/domain-context"],
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
    enabled: !isIsolatedSideEventsHost,
  });

  const value = useMemo<DomainContextValue>(() => {
    const domain = data?.domainContext || defaultDomain;

    const isRouteAllowed = (path: string): boolean => {
      if (domain.blockedRoutes?.length) {
        for (const blocked of domain.blockedRoutes) {
          if (matchRoute(blocked, path)) return false;
        }
      }

      // These public Capital pages are part of the application contract even when
      // an older persisted tenant allow-list predates the Startup Benefits launch.
      if (domain.contentArea === "capital" && isStartupBenefitsPublicPath(path)) {
        return true;
      }

      if (domain.allowedRoutes?.length) {
        for (const allowed of domain.allowedRoutes) {
          if (matchRoute(allowed, path)) return true;
        }
        if (path.startsWith("/admin")) return true;
        if (path.startsWith("/api")) return true;
        return false;
      }

      return true;
    };

    return {
      domain,
      isLoading: isIsolatedSideEventsHost ? false : isLoading,
      isMainSite: domain.contentArea === "main",
      isEwit: domain.contentArea === "ewit",
      isCohortDomain: domain.contentArea === "cohort",
      isChiefInAI: domain.contentArea === "chiefinai",
      isRouteAllowed,
    };
  }, [data, isLoading, isIsolatedSideEventsHost]);

  return (
    <DomainContext.Provider value={value}>
      {children}
    </DomainContext.Provider>
  );
}

export function useDomain() {
  return useContext(DomainContext);
}
